import { DeleteOutlined } from '@ant-design/icons';
import { Button, Input, Popconfirm, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api } from '@/api/client';
import { useApiMutation, useDebouncedValue, usePaged } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';
import { DepartmentFilter } from '@/components/DepartmentSelect';
import { createdAtColumn } from '@/components/createdAtColumn';
import { activeParam, listParam, StatusFilter } from '@/components/StatusFilter';
import { StickyTable } from '@/components/StickyTable';
import { TableCard } from '@/components/TableCard';

import { DriverDrawer } from './drivers/DriverDrawer';

interface DriverRow {
  id: number;
  employeeId: number;
  createdAt: string;
  employee: {
    id: number;
    personnelNumber: string;
    lastName: string;
    firstName: string;
    middleName: string | null;
    isActive: boolean;
    department: { name: string } | null;
    position: { name: string } | null;
  };
  /** Последнее по сроку удостоверение — сервер отдаёт одно. */
  licenses: Array<{ expiresAt: string; categories: string[] }>;
  permits: Array<{ expiresAt: string }>;
}

/** Ближайший истекающий документ — колонка, ради которой список и открывают. */
function nearestExpiry(row: DriverRow): string | null {
  const dates = [...row.licenses, ...row.permits].map((d) => d.expiresAt).sort();
  return dates[0] ?? null;
}

export function DriversPage() {
  const { can } = useAuth();
  const { t } = useTranslation();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput);
  const [departmentId, setDepartmentId] = useState<number[]>([]);
  const [detailId, setDetailId] = useState<number | null>(null);

  // Статус — признак «работает» у сотрудника за этим профилем.
  const [activity, setActivity] = useState<Array<'active' | 'dismissed'>>([]);
  const isActive = activeParam(activity);

  const query = usePaged<DriverRow>(['drivers'], '/drivers', {
    page,
    pageSize,
    search: search || undefined,
    departmentId: listParam(departmentId),
    isActive,
  });

  const remove = useApiMutation(
    async (id: number) => (await api.delete(`/drivers/${id}`)).data,
    { successMessage: t('Водительский профиль отозван'), invalidate: [['drivers'], ['office-summary']] },
  );

  if (!can(PERMISSIONS.DRIVER_READ)) {
    return <Typography.Text type="danger">{t('Нет прав на просмотр водителей')}</Typography.Text>;
  }

  return (
    <TableCard
      title={t('Водители')}
      extra={
        <Space wrap>
          <Input.Search
            allowClear
            placeholder={t('Фамилия или табельный номер')}
            style={{ width: 260 }}
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(1);
            }}
          />
          <DepartmentFilter
            value={departmentId}
            onChange={(value) => {
              setDepartmentId(value);
              setPage(1);
            }}
          />
          <StatusFilter
            width={170}
            value={activity}
            onChange={(value) => {
              setActivity(value);
              setPage(1);
            }}
            options={[
              { value: 'active', label: t('Работает'), color: 'green' },
              { value: 'dismissed', label: t('Уволен'), color: 'default' },
            ]}
          />
        </Space>
      }
    >
      <StickyTable<DriverRow>
        rowKey="id"
        exportSource={{
          url: '/drivers',
          params: { search: search || undefined, departmentId: listParam(departmentId), isActive },
        }}
        rowNumbers
        loading={query.isLoading}
        dataSource={query.data?.items ?? []}
        onRow={(row) => ({ onClick: () => setDetailId(row.id), style: { cursor: 'pointer' } })}
        pagination={{
          current: page,
          pageSize,
          total: query.data?.meta.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${t('Всего:')} ${total}`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
        columns={[
          {
            title: t('ФИО'),
            render: (_: unknown, row: DriverRow) => (
              <span className="tms-cell-key">
                {`${row.employee.lastName} ${row.employee.firstName} ${row.employee.middleName ?? ''}`.trim()}
              </span>
            ),
          },
          { title: t('Табельный'), dataIndex: ['employee', 'personnelNumber'], width: 120 },
          {
            title: t('Подразделение'),
            render: (_: unknown, row: DriverRow) => row.employee.department?.name ?? '—',
          },
          {
            title: t('Должность'),
            render: (_: unknown, row: DriverRow) => row.employee.position?.name ?? '—',
          },
          {
            title: t('Категории прав'),
            width: 150,
            render: (_: unknown, row: DriverRow) => {
              const license = row.licenses[0];
              if (!license || license.categories.length === 0) {
                return <Typography.Text type="secondary">—</Typography.Text>;
              }
              // Текстом через запятую, а не тегами: выгрузка в Excel берёт
              // текст ячейки, и теги склеились бы в «BCCE».
              const text = license.categories.join(', ');
              return dayjs(license.expiresAt).isBefore(dayjs(), 'day') ? (
                <Tooltip title={t('Удостоверение просрочено')}>
                  <Typography.Text type="danger">
                    {text} {t('(просрочено)')}
                  </Typography.Text>
                </Tooltip>
              ) : (
                <span className="tms-cell-key">{text}</span>
              );
            },
          },
          {
            title: t('Ближайший срок'),
            width: 170,
            render: (_: unknown, row: DriverRow) => {
              const date = nearestExpiry(row);
              if (!date) return <Tag color="red">{t('нет документов')}</Tag>;
              const daysLeft = dayjs(date).diff(dayjs(), 'day');
              const color = daysLeft < 0 ? 'red' : daysLeft <= 30 ? 'orange' : 'green';
              return (
                <Tag color={color}>
                  {dayjs(date).format('DD.MM.YYYY')}
                  {daysLeft < 0 ? ` ${t('(просрочен)')}` : ''}
                </Tag>
              );
            },
          },
          {
            title: t('Статус'),
            width: 110,
            render: (_: unknown, row: DriverRow) => (
              <Tag color={row.employee.isActive ? 'green' : 'default'}>
                {row.employee.isActive ? t('Работает') : t('Уволен')}
              </Tag>
            ),
          },
          createdAtColumn<DriverRow>(t),
          {
            title: '',
            width: 50,
            render: (_: unknown, row: DriverRow) => (
              <Space size={0} onClick={(event) => event.stopPropagation()}>
                {can(PERMISSIONS.DRIVER_DELETE) && (
                  <Popconfirm
                    title={t('Отозвать водительский профиль?')}
                    okText={t('Отозвать')}
                    cancelText={t('Отмена')}
                    onConfirm={() => remove.mutate(row.id)}
                  >
                    <Tooltip title={t('Отозвать водительский профиль')}>
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Tooltip>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />

      <DriverDrawer driverId={detailId} onClose={() => setDetailId(null)} />
    </TableCard>
  );
}
